"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CacheProvider } from "@chakra-ui/next-js";
import { ChakraProvider } from "@chakra-ui/react";
import {
  applyBrandingToDom,
  buildChakraBrandTheme,
  DEFAULT_BRANDING,
  type Branding,
  type PublicSettings,
} from "@/lib/branding";
import { publicApiGet } from "@/lib/public-api";

type BrandingContextValue = {
  publicSettings: PublicSettings | null;
  activeBranding: Branding;
  refreshBranding: () => Promise<void>;
  previewBranding: (branding?: Branding | null) => void;
};

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [publicSettings, setPublicSettings] = useState<PublicSettings | null>(
    null,
  );
  const [preview, setPreview] = useState<Branding | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const activeBranding = useMemo<Branding>(() => {
    // Keep SSR and first client render identical
    if (!mounted) return DEFAULT_BRANDING;
    return preview || publicSettings?.branding || DEFAULT_BRANDING;
  }, [mounted, preview, publicSettings]);

  const theme = useMemo(() => {
    return buildChakraBrandTheme(activeBranding);
  }, [activeBranding]);

  const refreshBranding = useCallback(async () => {
    try {
      const data = await publicApiGet<PublicSettings>("/settings/public");
      setPublicSettings(data);
      setPreview(null);

      if (mounted) {
        applyBrandingToDom(data?.branding || DEFAULT_BRANDING);
      }
    } catch {
      if (mounted) {
        applyBrandingToDom(DEFAULT_BRANDING);
      }
    }
  }, [mounted]);

  const previewBranding = useCallback(
    (branding?: Branding | null) => {
      const next = branding || null;
      setPreview(next);

      if (!mounted) return;

      applyBrandingToDom(
        next || publicSettings?.branding || DEFAULT_BRANDING,
      );
    },
    [mounted, publicSettings],
  );

  useEffect(() => {
    if (!mounted) return;
    refreshBranding();
  }, [mounted, refreshBranding]);

  useEffect(() => {
    if (!mounted) return;
    applyBrandingToDom(activeBranding);
  }, [mounted, activeBranding]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      publicSettings,
      activeBranding,
      refreshBranding,
      previewBranding,
    }),
    [publicSettings, activeBranding, refreshBranding, previewBranding],
  );

  return (
    <BrandingContext.Provider value={value}>
      <CacheProvider>
        <ChakraProvider theme={theme}>{children}</ChakraProvider>
      </CacheProvider>
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}